import { ReactNode, ButtonHTMLAttributes } from 'react';

interface ButtonBaseProps {
  variant?: 'primary' | 'secondary' | 'outline' | 'danger' | 'link' | 'ghost';
  size?: 'sm' | 'md' | 'lg' | 'icon';
  loading?: boolean;
  children: ReactNode;
  className?: string;
}

interface ButtonAsButton extends ButtonBaseProps, Omit<ButtonHTMLAttributes<HTMLButtonElement>, keyof ButtonBaseProps> {
  href?: never;
  target?: never;
  rel?: never;
}

interface ButtonAsLink extends ButtonBaseProps {
  href: string;
  target?: string;
  rel?: string;
  onClick?: never;
  disabled?: never;
  type?: never;
}

type ButtonProps = ButtonAsButton | ButtonAsLink;

export function Button({
  variant = 'primary',
  size = 'md',
  loading = false,
  className = '',
  children,
  ...props
}: ButtonProps) {
  const baseStyles = 'rounded-md font-bold cursor-pointer';

  const variantStyles = {
    primary: 'bg-orange-600 text-white border border-orange-600 disabled:opacity-50',
    secondary: 'bg-white text-gray-700 border border-gray-300 disabled:opacity-50',
    outline: 'bg-transparent text-[#000000] border border-[#000000] disabled:opacity-50',
    danger: 'bg-red-600 text-white border border-red-600 disabled:opacity-50',
    link: 'bg-transparent text-blue-600 border-none underline-offset-2 p-0 cursor-pointer',
    ghost: 'bg-transparent text-gray-500 border-none disabled:opacity-50',
  };

  const sizeStyles = {
    sm: 'px-4 py-2 text-sm',
    md: 'px-6 py-3 text-base',
    lg: 'px-8 py-4 text-lg',
    icon: 'p-1.5',
  };

  const sizeClass = variant === 'link' ? '' : sizeStyles[size];
  const classes = `${baseStyles} ${variantStyles[variant]} ${sizeClass} ${className}`;

  const content = loading ? (
    <span className="flex items-center justify-center gap-2">
      <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
      {children}
    </span>
  ) : children;

  // Render as <a> if href is provided
  if ('href' in props && props.href) {
    const { href, target, rel, ...rest } = props as ButtonAsLink;
    return (
      <a href={href} target={target} rel={rel} className={classes} {...rest as Record<string, unknown>}>
        {content}
      </a>
    );
  }

  const { disabled, ...buttonProps } = props as ButtonAsButton;
  return (
    <button
      className={classes}
      disabled={disabled || loading}
      {...buttonProps}
    >
      {content}
    </button>
  );
}
